/**
 * Star Rating Component
 * Interactive star rating input or display-only rating
 */

import { useState } from "react";
import { Star } from "lucide-react";
import { cn } from "@/utils/cn";

interface StarRatingProps {
  /** Current rating value (1-5) */
  value: number;
  /** Callback when rating changes (omit for display-only) */
  onChange?: (rating: number) => void;
  /** Size of the stars */
  size?: "sm" | "md" | "lg";
  /** Whether to show the numeric value */
  showValue?: boolean;
  /** Number of ratings for display mode */
  ratingCount?: number;
  /** Additional className */
  className?: string;
  /** Disable interaction */
  disabled?: boolean;
}

const sizeClasses = {
  sm: "w-4 h-4",
  md: "w-5 h-5",
  lg: "w-6 h-6",
};

const textSizeClasses = {
  sm: "text-xs",
  md: "text-sm",
  lg: "text-base",
};

export function StarRating({
  value,
  onChange,
  size = "md",
  showValue = false,
  ratingCount,
  className,
  disabled = false,
}: StarRatingProps) {
  const [hoverValue, setHoverValue] = useState<number | null>(null);

  const isInteractive = !!onChange && !disabled;
  const displayValue = hoverValue ?? value;

  const handleClick = (rating: number) => {
    if (isInteractive) {
      onChange(rating);
    }
  };

  const handleMouseEnter = (rating: number) => {
    if (isInteractive) {
      setHoverValue(rating);
    }
  };

  const handleMouseLeave = () => {
    if (isInteractive) {
      setHoverValue(null);
    }
  };

  return (
    <div className={cn("flex items-center gap-1", className)}>
      <div
        className={cn("flex", isInteractive && "cursor-pointer")}
        onMouseLeave={handleMouseLeave}
      >
        {[1, 2, 3, 4, 5].map((star) => {
          const isFilled = star <= displayValue;
          const isHalfFilled = !isFilled && star - 0.5 <= displayValue;

          return (
            <button
              key={star}
              type="button"
              onClick={() => handleClick(star)}
              onMouseEnter={() => handleMouseEnter(star)}
              disabled={!isInteractive}
              className={cn(
                "transition-colors focus:outline-none",
                isInteractive &&
                  "hover:scale-110 focus:ring-2 focus:ring-yellow-400 rounded",
              )}
              aria-label={`Rate ${star} star${star > 1 ? "s" : ""}`}
            >
              <Star
                className={cn(
                  sizeClasses[size],
                  "transition-colors",
                  isFilled
                    ? "fill-yellow-400 text-yellow-400"
                    : isHalfFilled
                      ? "fill-yellow-400/50 text-yellow-400"
                      : "fill-transparent text-gray-300",
                )}
              />
            </button>
          );
        })}
      </div>

      {showValue && value > 0 && (
        <span
          className={cn(
            textSizeClasses[size],
            "text-gray-600 dark:text-gray-400 ml-1",
          )}
        >
          {value.toFixed(1)}
        </span>
      )}

      {ratingCount !== undefined && (
        <span
          className={cn(
            textSizeClasses[size],
            "text-gray-500 dark:text-gray-500",
          )}
        >
          ({ratingCount})
        </span>
      )}
    </div>
  );
}
